import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { FileDown, FileSpreadsheet, FileType, Presentation, X } from 'lucide-react';
import { AudienceLevel, Cohort } from '../../types/pathway';
import { PathwayDocument } from './PathwayDocument';
import { PathwayButton } from './PathwayButton';
import { printPathwayDocument } from './PrintSurface';
interface DocumentViewerProps {
  open: boolean;
  pathwayName: string;
  company: string;
  cohort: Cohort | null;
  audience: AudienceLevel | 'any';
  items: string[];
  fields: string[];
  onClose: () => void;
  onDownloadWord: () => void;
  onDownloadCsv: () => void;
  onDownloadPowerPoint: () => void;
}
export function DocumentViewer({
  open,
  pathwayName,
  company,
  cohort,
  audience,
  items,
  fields,
  onClose,
  onDownloadWord,
  onDownloadCsv,
  onDownloadPowerPoint
}: DocumentViewerProps) {
  useEffect(() => {
    if (!open) return;
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [open, onClose]);
  if (!open) return null;
  return <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-60 p-4" role="dialog" aria-modal="true" aria-labelledby="document-viewer-title" onClick={onClose} data-id="element-1534" data-mp="214eepum46kch">
      <motion.div initial={{
      opacity: 0,
      scale: 0.95
    }} animate={{
      opacity: 1,
      scale: 1
    }} transition={{
      type: 'spring',
      stiffness: 300,
      damping: 30
    }} onClick={event => event.stopPropagation()} className="flex h-full max-h-[calc(100vh-2rem)] w-full max-w-[1100px] flex-col overflow-hidden rounded-xl bg-white shadow-2xl" data-id="element-1535" data-mp="2swc3t30xfhj2">
      <div className="flex flex-shrink-0 items-center justify-between gap-4 border-b border-gray-200 bg-white px-6 py-4" data-id="element-1536" data-mp="3btmz6cjftqt8">
        <div className="min-w-0" data-id="element-1537" data-mp="3glwvqbf25p4h">
          <h2 id="document-viewer-title" className="truncate text-lg font-bold text-gray-900" data-id="element-1538" data-mp="3gmzn0kgaltkx">
            {pathwayName || 'Recommended Learning Pathway'}
          </h2>
          <p className="text-sm text-gray-600" data-id="element-1539" data-mp="3md5r93euc6rk">
            Document preview · what the prospect receives
          </p>
        </div>
        <div className="flex flex-shrink-0 items-center gap-3" data-id="element-1540" data-mp="3chlzmt7b7u8m">
          <PathwayButton variant="secondary" size="sm" onClick={onDownloadPowerPoint} className="min-w-0 px-4" data-id="element-1541" data-mp="3cj6tshpppb5y">
            <Presentation size={16} data-id="element-1542" data-mp="32qhns2cxkpi0" />
            PowerPoint
          </PathwayButton>
          <PathwayButton variant="secondary" size="sm" onClick={onDownloadWord} className="min-w-0 px-4" data-id="element-1543" data-mp="2yqqyk331yhfl">
            <FileType size={16} data-id="element-1544" data-mp="2or8ihxq9jfst" />
            Word
          </PathwayButton>
          <PathwayButton variant="secondary" size="sm" onClick={onDownloadCsv} className="min-w-0 px-4" data-id="element-1545" data-mp="2otciw5oydwoc">
            <FileSpreadsheet size={16} data-id="element-1546" data-mp="29nowthozbofz" />
            CSV
          </PathwayButton>
          <PathwayButton size="sm" onClick={() => printPathwayDocument(pathwayName)} data-id="element-1547" data-mp="2emrzwwlefklb">
            <FileDown size={16} data-id="element-1548" data-mp="2jn7etslbx05e" />
            Save as PDF
          </PathwayButton>
          <button type="button" onClick={onClose} className="text-sm font-semibold text-gray-700 underline transition-colors hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#30718D]" data-id="element-1549" data-mp="2okpqhqqqdth3">
            Close
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-auto bg-gray-900/80 p-8" data-id="element-1550" data-mp="20ven01hzxkz9">
        <div className="mx-auto w-max" data-id="element-1551" data-mp="25nes08cxb42p">
          <PathwayDocument pathwayName={pathwayName} company={company} cohort={cohort} audience={audience} items={items} fields={fields} data-id="element-1552" data-mp="25og1i8eajiib" />
        </div>
      </div>
      </motion.div>
    </div>;
}